// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { IconButton, Snackbar, SnackbarContent, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import themev5 from '@core/themev5';

export enum NotificationType {
  info,
  error
}

// Prop interface for main class.
export interface NotificationProps {
  className?: string;
  autoHideDuration?: number | null;
}

// State interface for main class.
interface NotificationState {
  message: React.ReactNode;
  type: NotificationType;
  notificationProps: Partial<NotificationProps>;
  open: boolean;
}

const defaultProps: NotificationProps = {
  autoHideDuration: 5000,
  className: '',
};


let showNotificationFn: (message: React.ReactNode, type: NotificationType, notificationProps?: NotificationProps) => void;
let hideNotificationFn: () => void;
let isOpenNotificationFn: () => boolean;

export function showNotification(
  message: React.ReactNode, 
  type: NotificationType = NotificationType.info, 
  notificationProps?: NotificationProps) {
  showNotificationFn(message, type, notificationProps);
}

export function hideNotification() {
  hideNotificationFn();
}

export function isOpenNotification(): boolean {
  return isOpenNotificationFn();
}

// Class to display a popup notification.
class Notification extends React.Component<NotificationProps, NotificationState> {

  readonly state = {
    message: '',
    type: NotificationType.info,
    notificationProps: defaultProps,
    open: false,
  };

  // Load the Fn functions (seem to be identical to the exported functions)
  componentDidMount() {
    showNotificationFn = this.open;
    hideNotificationFn = this.close;
    isOpenNotificationFn = this.isOpenNotification;
  }

  private isOpenNotification = () => this.state.open;

  // Configure the notification box and set flag to open it.
  private open = (message: React.ReactNode, type: NotificationType = NotificationType.info, notificationProps: NotificationProps = defaultProps) => {
    const classes = withStyles.getClasses(this.props);
    // This section does not seem to do anything, and sometimes shows a warning in the browswer dev log.
    // const icon = type === NotificationType.error ? ErrorIcon : null;
    message = (
      <div className={classes.snackbarContentWrapper}> 
        {type === NotificationType.error ? <ErrorIcon style={{color: themev5.palette.colors.error}} /> : null}
        {message} 
      </div> 
    );
    

    this.setState({
      message,
      type,
      notificationProps: {
        ...defaultProps,
        ...notificationProps,
      },
      open: true,
    });
  }

  private close = () => {
    this.setState({ notificationProps: defaultProps, open: false });
  }

  render() {
    const classes = withStyles.getClasses(this.props);
    const { message, type, notificationProps, open } = this.state;
    const { autoHideDuration } = notificationProps;

    return (
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        autoHideDuration={autoHideDuration}
        open={open}
        TransitionComponent={Fade}
        onClose={this.close}
      >
        <SnackbarContent
          className={classes[type]}
          message={message}
          action={(
            <IconButton disableRipple className={classes.closeButton} onClick={this.close}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          )}
        />
      </Snackbar>
    );
  }
}

const NotificationStyled = withStyles(
  Notification, 
  (theme, props) => ({
    "root": {},
    "icon": {
      fontSize: 16,
      marginRight: theme.spacing(2),
    },
    "closeButton": {
      padding: 0,

      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    "closeIcon": {
      fontSize: 20
    },
    "snackbarContentWrapper": {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        marginRight: theme.spacing(3)
      }
    },
    [NotificationType.error]: {
      border: `2px solid ${theme.palette.colors.error}`
    },
    [NotificationType.info]: {},
  })
);

export default NotificationStyled;